import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, CornerDownLeft, Loader2, X } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

/**
 * Reusable natural-language "Ask" assistant panel.
 * Props:
 *  - endpoint: full URL to POST { question, session_id } -> { answer, citations[], session_id }
 *  - title, placeholder, example
 *  - metaFor(citation) -> optional small subtitle string per citation card
 */
export function AskAssistant({ endpoint, title = "Ask the assistant", placeholder = "Ask a question…", example = "", metaFor, testid = "ask-assistant" }) {
  const [q, setQ] = useState("");
  const [asking, setAsking] = useState(false);
  const [thread, setThread] = useState([]);
  const [sessionId, setSessionId] = useState(null);

  const ask = async () => {
    const question = q.trim();
    if (!question || asking) return;
    setAsking(true);
    setThread((t) => [...t, { role: "user", content: question }]);
    setQ("");
    try {
      const r = await axios.post(endpoint, { question, session_id: sessionId });
      setSessionId(r.data.session_id);
      setThread((t) => [...t, { role: "assistant", content: r.data.answer, citations: r.data.citations || [] }]);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Assistant unavailable";
      toast.error(msg);
      setThread((t) => [...t, { role: "assistant", content: msg, citations: [], error: true }]);
    } finally { setAsking(false); }
  };
  const newChat = () => { setThread([]); setSessionId(null); setQ(""); };

  return (
    <Card className="bg-[#FFFCF2] border-[#ECE1C2] shadow-card rounded-xl overflow-hidden" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2.5">
          <Sparkles className="w-4 h-4 text-[#B8860B]" />
          <span className="text-[13px] font-semibold text-[#1A1A1A]">{title}</span>
          {thread.length === 0 && example && <span className="text-[11px] text-[#8A857A]">e.g. "{example}"</span>}
          {thread.length > 0 && (
            <button onClick={newChat} className="ml-auto text-[11px] text-[#8A857A] hover:text-[#1A1A1A] flex items-center gap-1" data-testid={`${testid}-newchat`}><X className="w-3 h-3" /> New chat</button>
          )}
        </div>

        {thread.length > 0 && (
          <div className="mb-3 space-y-2.5 max-h-[360px] overflow-y-auto pr-1" data-testid={`${testid}-thread`}>
            {thread.map((m, i) => (
              m.role === "user" ? (
                <div key={i} className="flex justify-end">
                  <div className="bg-[#FFD108] text-[#1A1A1A] rounded-2xl rounded-br-sm px-3.5 py-2 text-[13px] font-medium max-w-[80%]" data-testid={`${testid}-user-${i}`}>{m.content}</div>
                </div>
              ) : (
                <div key={i} className="flex justify-start">
                  <div className="bg-white border border-[#E6E2D9] rounded-2xl rounded-bl-sm px-3.5 py-2.5 max-w-[90%] shadow-sm" data-testid={`${testid}-assistant-${i}`}>
                    <p className={`text-[13.5px] leading-relaxed whitespace-pre-wrap ${m.error ? "text-[#C23A00]" : "text-[#2A2A2A]"}`}>{m.content}</p>
                    {m.citations?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {m.citations.map((c, ci) => (
                          <span key={c.id || ci} className="inline-flex items-center gap-1.5 rounded-md bg-[#FBFAF7] border border-[#E6E2D9] px-2 py-1 text-[11px]" data-testid={`${testid}-cite-${ci}`}>
                            <span className="font-semibold text-[#1A1A1A]">{c.name}</span>
                            {metaFor && metaFor(c) && <span className="text-[#8A857A]">· {metaFor(c)}</span>}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )
            ))}
            {asking && (
              <div className="flex justify-start"><div className="bg-white border border-[#E6E2D9] rounded-2xl rounded-bl-sm px-3.5 py-2.5 flex items-center gap-2 text-[#8A857A] text-[13px]"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…</div></div>
            )}
          </div>
        )}

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !asking) ask(); }} placeholder={thread.length ? "Ask a follow-up…" : placeholder} className="bg-white border-[#DAD5CA] text-[#1A1A1A] placeholder:text-[#8A857A] pr-9" data-testid={`${testid}-input`} />
            <CornerDownLeft className="w-3.5 h-3.5 text-[#B8B2A5] absolute right-3 top-1/2 -translate-y-1/2" />
          </div>
          <Button onClick={ask} disabled={asking || !q.trim()} className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] font-semibold" data-testid={`${testid}-btn`}>
            {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            <span className="ml-1.5">Ask</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
